import {
	faCircleInfo,
	faFire,
	faPencil,
	faTableColumns,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SavedSearchModel } from '@supply-mover-2-0/core';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { Tooltip } from '../../../components/overlays/Tooltip';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { tailwindClasses } from '../../../themes/tailwind-classes';

interface SavedSearchItemProps {
	search: SavedSearchModel;
	isSelected: boolean;
	onSelect: () => void;
	onEdit: (search: SavedSearchModel) => void;
	onEditColumns?: (search: SavedSearchModel) => void;
}

const TYPE_BADGE_STYLES = {
	default: 'bg-subtle text-muted',
	mine: tailwindClasses.badgeInfo,
	shared: tailwindClasses.badgeSuccess,
};

const TYPE_LABELS = {
	default: 'Default',
	mine: 'Mine',
	shared: 'Shared',
};

/**
 * Format usage count for display.
 * Shows full number up to 999, then "1k+", "10k+", etc.
 */
function formatUseCount(count: number): string {
	if (count < 1000) {
		return String(count);
	}
	if (count < 10000) {
		return `${Math.floor(count / 1000)}k+`;
	}
	return `${Math.floor(count / 1000)}k+`;
}

export function SavedSearchItem({
	search,
	isSelected,
	onSelect,
	onEdit,
	onEditColumns,
}: SavedSearchItemProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	// Edit button shows for non-default searches where user has edit permission
	// canEdit is true when: user is manager OR (allowEdit is true AND user has access)
	// Rendered through ternaries below rather than `&&`: `canEdit` is typed
	// boolean but can arrive from the API as a tinyint 0/1, and `{0 && …}`
	// renders a literal "0" in the row (PD-15581).
	const showEditButton = search.type !== 'default' && search.canEdit;

	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onSelect}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect();
				}
			}}
			className={clsx(
				'grid items-center px-3 py-2 cursor-pointer',
				'transition-colors duration-150',
				{
					'bg-primary-50 dark:bg-primary-900/30': isSelected,
					'hover:bg-surface-hover': !isSelected,
				},
			)}
			style={{ gridTemplateColumns: '1fr 50px 60px 32px 32px' }}
		>
			<span
				className={clsx(
					'flex items-center gap-1.5 text-sm font-medium truncate',
					colors.classes.textPrimary,
				)}
			>
				{t(search.name)}
				{search.description != null && search.description.length > 0 && (
					<Tooltip content={t(search.description)} position="top">
						<span className="flex-shrink-0">
							<FontAwesomeIcon
								icon={faCircleInfo}
								className={clsx('h-3 w-3', colors.classes.textMuted)}
							/>
						</span>
					</Tooltip>
				)}
			</span>

			{/* Popularity indicator (usage count) - orange badge with white text */}
			<div className="flex justify-center">
				{search.totalUseCount > 0 && (
					<div
						className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-warning-solid text-white rounded"
						title={t('Popularity')}
					>
						<FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5" />
						<span>{formatUseCount(search.totalUseCount)}</span>
					</div>
				)}
			</div>

			{/* Type badge */}
			<div className="flex justify-center">
				<span
					className={clsx(
						'px-2 py-0.5 text-xs font-medium rounded',
						TYPE_BADGE_STYLES[search.type],
					)}
				>
					{t(TYPE_LABELS[search.type])}
				</span>
			</div>

			{/* Edit button - opens edit modal */}
			<div className="flex justify-center">
				{showEditButton ? (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onEdit(search);
						}}
						title={t('Edit search')}
						className={clsx(
							'p-0.5 rounded text-faint',
							'hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30',
							'transition-colors duration-150',
							'focus:outline-none focus:ring-2 focus:ring-primary-500',
						)}
					>
						<FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
					</button>
				) : null}
			</div>

			{/* Edit columns button - opens column editor */}
			<div className="flex justify-center">
				{showEditButton && onEditColumns != null ? (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onEditColumns(search);
						}}
						title={t('Edit columns')}
						className={clsx(
							'p-0.5 rounded text-faint',
							'hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30',
							'transition-colors duration-150',
							'focus:outline-none focus:ring-2 focus:ring-primary-500',
						)}
					>
						<FontAwesomeIcon icon={faTableColumns} className="h-3 w-3" />
					</button>
				) : null}
			</div>
		</div>
	);
}
